import { useState, useRef, useCallback } from "react";

const toBase64 = (file) =>
  new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result.split(",")[1]);
    r.onerror = () => rej(new Error("Read failed"));
    r.readAsDataURL(file);
  });

const TABS = ["Documents", "General Information", "Timeline"];

const EMPTY_FORM = {
  tenderTitle: "", referenceNumber: "", department: "",
  projectLocation: "", issuingAuthority: "", description: "",
  issueDate: "", clarificationDeadline: "", submissionDeadline: "",
  openingDate: "", expectedAwardDate: "", tenderValidity: "",
  contractDuration: "", currency: "OMR",
};

const GEMINI_API_KEY = "AIzaSyC3SZeOmzHvPjfnTB3sBmMR0cZJBOZbvfE";
const GEMINI_URL = "https://generativelanguage.googleapis.com/v1beta/models/gemini-3-flash-preview:generateContent";

const GEMINI_PROMPT = `You are an expert tender document analyst. Analyze ALL provided tender/RFP documents (may be English, Arabic, or both).

RULES:
- Return null for any field not explicitly found — never guess
- Dates must be YYYY-MM-DD if determinable, otherwise return the raw text found
- description: 150-200 word summary of what the vendor must deliver
- Translate any Arabic content to English
- submissionDeadline may appear in a separate document — check all files

Return ONLY a raw JSON object, no markdown, no backticks, no explanation:

{
  "tenderTitle": null,
  "referenceNumber": null,
  "issuingAuthority": null,
  "projectLocation": null,
  "description": null,
  "issueDate": null,
  "clarificationDeadline": null,
  "submissionDeadline": null,
  "openingDate": null,
  "expectedAwardDate": null,
  "tenderValidity": null,
  "contractDuration": null,
  "currency": null
}`;

// ── Shared UI components ──────────────────────────────────────────────────────

function TabBar({ active, onChange }) {
  return (
    <div style={{ display: "flex", borderBottom: "1px solid #e5e7eb", marginBottom: 24 }}>
      {TABS.map((t) => (
        <button key={t} onClick={() => onChange(t)} style={{
          padding: "10px 16px", border: "none", background: "none", cursor: "pointer",
          fontSize: 13, fontWeight: active === t ? 600 : 400,
          color: active === t ? "#d97706" : "#6b7280",
          borderBottom: active === t ? "2px solid #d97706" : "2px solid transparent",
          whiteSpace: "nowrap",
        }}>{t}</button>
      ))}
    </div>
  );
}

function Field({ label, children, half }) {
  return (
    <div style={{ gridColumn: half ? "span 1" : "span 2", display: "flex", flexDirection: "column", gap: 4 }}>
      <label style={{ fontSize: 12, fontWeight: 500, color: "#374151" }}>{label}</label>
      {children}
    </div>
  );
}

function Input({ value, onChange, placeholder, ai }) {
  return (
    <div style={{ position: "relative" }}>
      <input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          width: "100%", padding: "8px 10px", boxSizing: "border-box",
          border: `1px solid ${ai ? "#fbbf24" : "#d1d5db"}`, borderRadius: 6,
          fontSize: 13, background: ai ? "#fffbeb" : "#fff", outline: "none",
        }}
      />
      {ai && (
        <span style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", fontSize: 10, color: "#d97706", fontWeight: 700 }}>
          AI ✦
        </span>
      )}
    </div>
  );
}

function Textarea({ value, onChange, placeholder, rows = 5, ai }) {
  return (
    <div style={{ position: "relative" }}>
      <textarea
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        style={{
          width: "100%", padding: "8px 10px", boxSizing: "border-box",
          border: `1px solid ${ai ? "#fbbf24" : "#d1d5db"}`, borderRadius: 6,
          fontSize: 13, background: ai ? "#fffbeb" : "#fff", outline: "none",
          resize: "vertical", fontFamily: "inherit",
        }}
      />
      {ai && (
        <span style={{ position: "absolute", right: 8, top: 8, fontSize: 10, color: "#d97706", fontWeight: 700 }}>
          AI ✦
        </span>
      )}
    </div>
  );
}

function Select({ value, onChange, options }) {
  return (
    <select
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      style={{ padding: "8px 10px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13, background: "#fff", width: "100%", outline: "none" }}
    >
      <option value="">— Select —</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function TenderModal() {
  const [tab, setTab] = useState("Documents");
  const [files, setFiles] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [aiFields, setAiFields] = useState({});
  const [loading, setLoading] = useState(false);
  const [aiStatus, setAiStatus] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const fileInputRef = useRef();

  const setField = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const handleFiles = useCallback((incoming) => {
    const all = Array.from(incoming).filter((f) => f.type === "application/pdf");
    const ok = all.filter((f) => f.size <= 10 * 1024 * 1024);
    const big = all.filter((f) => f.size > 10 * 1024 * 1024);
    if (big.length) alert(`File(s) exceed 10 MB limit and were skipped:\n${big.map((f) => f.name).join("\n")}`);
    setFiles((prev) => {
      const seen = new Set(prev.map((p) => p.name));
      return [...prev, ...ok.filter((f) => !seen.has(f.name))];
    });
  }, []);

  const removeFile = (name) => setFiles((f) => f.filter((x) => x.name !== name));

  const reset = () => {
    setForm(EMPTY_FORM);
    setFiles([]);
    setAiFields({});
    setAiStatus("");
    setTab("Documents");
  };

  const testGeminiConnection = async () => {
    const logPrefix = "[TenderAI][testGeminiConnection]";
    console.log(`${logPrefix} starting simple connection test`);
    setAiStatus("Testing Gemini API connection…");

    try {
      const payload = {
        contents: [
          {
            parts: [
              {
                text: "Say hello. This is a connectivity test from the Tender AI tool.",
              },
            ],
          },
        ],
      };

      const res = await fetch(GEMINI_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-goog-api-key": GEMINI_API_KEY,
        },
        body: JSON.stringify(payload),
      });

      console.log(`${logPrefix} received response`, {
        status: res.status,
        ok: res.ok,
      });

      const data = await res.json();
      console.debug(`${logPrefix} parsed response JSON`, data);

      if (!res.ok) {
        const msg = data?.error?.message || `HTTP ${res.status}`;
        console.error(`${logPrefix} Gemini returned error`, {
          status: res.status,
          body: data,
        });
        setAiStatus(`Connection error: ${msg}`);
        return;
      }

      const text = data?.candidates?.[0]?.content?.parts?.[0]?.text || "";
      console.log(`${logPrefix} model replied`, text);
      setAiStatus(text ? `Connected to Gemini: "${text.slice(0, 140)}"` : "Connected to Gemini (empty text response)");
    } catch (err) {
      console.error(`${logPrefix} failed`, err);
      setAiStatus(`Connection error: ${err.message}`);
    }
  };

  // ── Gemini call ─────────────────────────────────────────────────────────────
  const runAI = async () => {
    const logPrefix = "[TenderAI][runAI]";

    console.log(`${logPrefix} invoked`, {
      fileCount: files.length,
      loading,
      fileMeta: files.map((f) => ({ name: f.name, size: f.size, type: f.type })),
    });

    if (!files.length) {
      console.warn(`${logPrefix} aborting: no files selected`);
      return;
    }

    if (loading) {
      console.warn(`${logPrefix} aborting: already loading in progress`);
      return;
    }

    setLoading(true);
    setAiStatus("Reading documents…");
    console.log(`${logPrefix} starting document read & encode phase`);

    try {
      const parts = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        setAiStatus(`Encoding file ${i + 1} of ${files.length}: ${file.name}`);
        console.log(`${logPrefix} encoding file`, {
          index: i,
          name: file.name,
          size: file.size,
          type: file.type,
        });

        let b64;
        try {
          b64 = await toBase64(file);
        } catch (encodeErr) {
          console.error(`${logPrefix} failed to encode file`, { name: file.name }, encodeErr);
          throw encodeErr;
        }

        parts.push({ inline_data: { mime_type: "application/pdf", data: b64 } });
      }

      console.log(`${logPrefix} finished encoding files`, {
        partsCount: parts.length,
      });

      parts.push({ text: GEMINI_PROMPT });
      console.log(`${logPrefix} appended prompt, sending request to Gemini`, {
        url: GEMINI_URL,
      });

      setAiStatus("Analyzing with Gemini AI…");

      const payload = {
        contents: [{ parts }],
        generationConfig: { temperature: 0.1 },
      };

      try {
        console.debug(
          `${logPrefix} request payload preview`,
          JSON.stringify(
            {
              ...payload,
              contents: [
                {
                  ...payload.contents[0],
                  parts: [
                    { inline_data: { mime_type: "application/pdf", data: "[omitted base64]" } },
                    { text: "[prompt omitted]" },
                  ],
                },
              ],
            },
            null,
            2,
          ).slice(0, 2000),
        );
      } catch (stringifyErr) {
        console.warn(`${logPrefix} failed to stringify payload for preview`, stringifyErr);
      }

      const res = await fetch(GEMINI_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-goog-api-key": GEMINI_API_KEY,
        },
        body: JSON.stringify(payload),
      });

      console.log(`${logPrefix} received response`, {
        status: res.status,
        ok: res.ok,
      });

      let data;
      try {
        data = await res.json();
      } catch (parseErr) {
        let rawText = null;
        try {
          rawText = await res.text();
        } catch (textErr) {
          console.error(`${logPrefix} failed to read raw response text after JSON parse error`, textErr);
        }
        console.error(`${logPrefix} failed to parse response JSON`, parseErr, {
          rawResponsePreview: rawText ? rawText.slice(0, 2000) : null,
        });
        throw parseErr;
      }

      console.debug(`${logPrefix} parsed response JSON`, data);

      if (!res.ok) {
        const msg = data?.error?.message || `HTTP ${res.status}`;
        console.error(`${logPrefix} Gemini returned error`, {
          status: res.status,
          body: data,
        });
        throw new Error(msg);
      }

      let raw = data?.candidates?.[0]?.content?.parts?.[0]?.text || "";
      console.log(`${logPrefix} raw model output (truncated)`, raw.slice(0, 2000));
      raw = raw.replace(/^```(?:json)?\s*/i, "").replace(/\s*```$/i, "").trim();

      let extracted;
      try {
        extracted = JSON.parse(raw);
      } catch (jsonErr) {
        console.error(`${logPrefix} failed to parse model JSON`, jsonErr, {
          rawPreview: raw.slice(0, 2000),
        });
        throw jsonErr;
      }

      const KEYS = [
        "tenderTitle", "referenceNumber", "issuingAuthority", "projectLocation",
        "description", "issueDate", "clarificationDeadline", "submissionDeadline",
        "openingDate", "expectedAwardDate", "tenderValidity", "contractDuration", "currency",
      ];

      const updates = {};
      const filled = {};
      for (const key of KEYS) {
        const v = extracted[key];
        if (v !== null && v !== undefined && String(v).trim() !== "" && String(v).toLowerCase() !== "null") {
          updates[key] = String(v).trim();
          filled[key] = true;
        } else {
          console.log(`${logPrefix} field missing or null in AI output`, { key, value: v });
        }
      }

      console.log(`${logPrefix} applying extracted fields`, {
        updates,
        filledKeys: Object.keys(filled),
      });

      setForm((f) => ({ ...f, ...updates }));
      setAiFields(filled);
      setAiStatus(`✓ AI filled ${Object.keys(filled).length} fields from ${files.length} document(s)`);
      setTab("General Information");
    } catch (err) {
      console.error("[TenderAI][runAI] failed", err);
      setAiStatus(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // ── Tab: Documents ──────────────────────────────────────────────────────────
  const renderDocuments = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Drop zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
        onClick={() => fileInputRef.current.click()}
        style={{
          border: `2px dashed ${dragOver ? "#d97706" : "#d1d5db"}`,
          borderRadius: 8, padding: "32px 16px", textAlign: "center",
          cursor: "pointer", background: dragOver ? "#fffbeb" : "#f9fafb", transition: "all 0.2s",
        }}
      >
        <div style={{ fontSize: 28, marginBottom: 8 }}>📎</div>
        <div style={{ fontSize: 13, color: "#6b7280" }}>
          Drag and drop files here or click to upload&nbsp;<strong>(max 10 MB per file)</strong>
        </div>
        <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>PDF only · Arabic &amp; English supported</div>
        <input
          ref={fileInputRef} type="file" accept=".pdf" multiple
          style={{ display: "none" }}
          onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }}
        />
      </div>

      {/* File list */}
      {files.length === 0 ? (
        <div style={{ padding: "12px 16px", border: "1px solid #e5e7eb", borderRadius: 6, color: "#9ca3af", fontSize: 13 }}>
          No documents yet.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {files.map((f) => (
            <div key={f.name} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 6 }}>
              <span>📄</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</div>
                <div style={{ fontSize: 11, color: "#9ca3af" }}>{(f.size / 1024 / 1024).toFixed(2)} MB</div>
              </div>
              <button onClick={() => removeFile(f.name)}
                style={{ background: "none", border: "none", cursor: "pointer", color: "#9ca3af", fontSize: 20, lineHeight: 1 }}>×</button>
            </div>
          ))}
        </div>
      )}

      {/* AI panel */}
      <div style={{ border: "1.5px dashed #fbbf24", borderRadius: 8, padding: 16, background: "#fffbeb" }}>
        <p style={{ margin: "0 0 4px", fontSize: 13, color: "#92400e", lineHeight: 1.5 }}>
          <strong>✦ AI Field Drafting</strong> — Upload tender documents and AI will extract the title, reference number,
          issuing authority, scope of work, all timeline dates, and more. Arabic documents are fully supported.
        </p>
        <p style={{ margin: "0 0 12px", fontSize: 12, color: "#b45309" }}>
          The submission deadline may appear in a separate notice — upload all related files for best results.
        </p>

        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <button
            onClick={runAI}
            disabled={files.length === 0 || loading}
            style={{
              padding: "8px 18px", border: "none", borderRadius: 20,
              background: files.length === 0 || loading ? "#e5e7eb" : "#1c1917",
              color: files.length === 0 || loading ? "#9ca3af" : "#fff",
              fontSize: 13, fontWeight: 500,
              cursor: files.length === 0 || loading ? "default" : "pointer",
              display: "flex", alignItems: "center", gap: 6,
            }}
          >
            {loading
              ? <><span style={{ display: "inline-block", animation: "spin 0.7s linear infinite" }}>⟳</span>&nbsp;{aiStatus}</>
              : <>✦ Draft fields from documents (AI)</>}
          </button>
          {!loading && files.length === 0 && (
            <span style={{ fontSize: 12, color: "#9ca3af" }}>Upload at least one document to enable.</span>
          )}
          <button
            type="button"
            onClick={testGeminiConnection}
            disabled={loading}
            style={{
              padding: "6px 14px",
              borderRadius: 16,
              border: "1px solid #d1d5db",
              background: "#fff",
              color: "#374151",
              fontSize: 12,
              cursor: loading ? "default" : "pointer",
            }}
          >
            Test Gemini connection
          </button>
        </div>

        {aiStatus && !loading && (
          <div style={{
            marginTop: 10, fontSize: 12, padding: "6px 10px", borderRadius: 5,
            color: aiStatus.startsWith("Error") ? "#dc2626" : "#065f46",
            background: aiStatus.startsWith("Error") ? "#fef2f2" : "#ecfdf5",
          }}>
            {aiStatus}
          </div>
        )}

        {Object.keys(aiFields).length > 0 && (
          <div style={{ marginTop: 8, fontSize: 11, color: "#92400e" }}>
            ✦ Fields highlighted in{" "}
            <span style={{ background: "#fffbeb", border: "1px solid #fbbf24", padding: "1px 6px", borderRadius: 3 }}>yellow</span>
            {" "}were pre-filled by AI — review and edit as needed.
          </div>
        )}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  // ── Tab: General Information ────────────────────────────────────────────────
  const renderGeneral = () => (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 16px" }}>
      <Field label="Tender Title">
        <Input value={form.tenderTitle} onChange={(v) => setField("tenderTitle", v)} placeholder="Tender title" ai={aiFields.tenderTitle} />
      </Field>
      <Field label="Tender Reference Number" half>
        <Input value={form.referenceNumber} onChange={(v) => setField("referenceNumber", v)} placeholder="e.g. 2026-DEP-TND-0001" ai={aiFields.referenceNumber} />
      </Field>
      <Field label="Department" half>
        <Select value={form.department} onChange={(v) => setField("department", v)}
          options={["IT", "Infrastructure", "Finance", "Operations", "Legal", "Procurement", "Aviation Safety"]} />
      </Field>
      <Field label="Project Location" half>
        <Input value={form.projectLocation} onChange={(v) => setField("projectLocation", v)} placeholder="Location" ai={aiFields.projectLocation} />
      </Field>
      <Field label="Issuing Authority" half>
        <Input value={form.issuingAuthority} onChange={(v) => setField("issuingAuthority", v)} placeholder="Authority" ai={aiFields.issuingAuthority} />
      </Field>
      <Field label="Description / Scope of Work">
        <Textarea value={form.description} onChange={(v) => setField("description", v)}
          placeholder="Scope of work, objectives, and key deliverables…" rows={6} ai={aiFields.description} />
      </Field>
    </div>
  );

  // ── Tab: Timeline ───────────────────────────────────────────────────────────
  const renderTimeline = () => (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 16px" }}>
      {[
        ["issueDate", "Issue Date"],
        ["clarificationDeadline", "Clarification Deadline"],
        ["submissionDeadline", "Submission Deadline"],
        ["openingDate", "Opening Date"],
        ["expectedAwardDate", "Expected Award Date"],
      ].map(([key, label]) => {
        const ai = aiFields[key];
        const val = form[key] || "";
        const isValidDate = /^\d{4}-\d{2}-\d{2}$/.test(val);
        return (
          <Field key={key} label={label} half>
            <div style={{ position: "relative" }}>
              <input
                type="date"
                value={isValidDate ? val : ""}
                onChange={(e) => setField(key, e.target.value)}
                style={{
                  width: "100%", padding: "8px 10px", boxSizing: "border-box",
                  border: `1px solid ${ai ? "#fbbf24" : "#d1d5db"}`, borderRadius: 6,
                  fontSize: 13, background: ai ? "#fffbeb" : "#fff", outline: "none",
                }}
              />
              {ai && (
                <span style={{ position: "absolute", right: 32, top: "50%", transform: "translateY(-50%)", fontSize: 10, color: "#d97706", fontWeight: 700 }}>
                  AI ✦
                </span>
              )}
            </div>
            {ai && val && !isValidDate && (
              <div style={{ fontSize: 11, color: "#b45309", marginTop: 2 }}>
                Found in document: "{val}" — please select date manually above
              </div>
            )}
          </Field>
        );
      })}

      <Field label="Tender Validity (days)" half>
        <Input value={form.tenderValidity} onChange={(v) => setField("tenderValidity", v)} placeholder="90" ai={aiFields.tenderValidity} />
      </Field>
      <Field label="Contract Duration">
        <Input value={form.contractDuration} onChange={(v) => setField("contractDuration", v)} placeholder="e.g. 3 years" ai={aiFields.contractDuration} />
      </Field>
    </div>
  );

  const tabContent = {
    "Documents": renderDocuments,
    "General Information": renderGeneral,
    "Timeline": renderTimeline,
  };
  const currentIndex = TABS.indexOf(tab);

  // ── Success screen ──────────────────────────────────────────────────────────
  if (submitted) {
    return (
      <div style={{ fontFamily: "system-ui, sans-serif", display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", background: "#f3f4f6" }}>
        <div style={{ background: "#fff", borderRadius: 12, padding: 48, textAlign: "center", maxWidth: 400, boxShadow: "0 4px 24px rgba(0,0,0,0.10)" }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
          <h2 style={{ margin: "0 0 8px", color: "#111827" }}>Tender Saved</h2>
          <p style={{ color: "#6b7280", fontSize: 14, margin: "0 0 20px" }}>
            {form.tenderTitle || "Tender"} has been added successfully.
          </p>
          <button onClick={() => { setSubmitted(false); reset(); }}
            style={{ padding: "10px 24px", background: "#d97706", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 14, fontWeight: 500 }}>
            Add Another Tender
          </button>
        </div>
      </div>
    );
  }

  // ── Main modal ──────────────────────────────────────────────────────────────
  return (
    <div style={{ fontFamily: "system-ui, sans-serif", display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", background: "#f3f4f6", padding: 16 }}>
      <div style={{ background: "#fff", borderRadius: 12, boxShadow: "0 4px 24px rgba(0,0,0,0.10)", width: "100%", maxWidth: 740, maxHeight: "92vh", display: "flex", flexDirection: "column" }}>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "20px 24px 0" }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: "#111827" }}>Add Tender</h2>
          <button onClick={reset}
            style={{ background: "none", border: "none", cursor: "pointer", fontSize: 22, color: "#9ca3af", lineHeight: 1 }}>×</button>
        </div>

        {/* Tabs */}
        <div style={{ padding: "0 24px" }}>
          <TabBar active={tab} onChange={setTab} />
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: "auto", padding: "0 24px 16px" }}>
          {tabContent[tab]?.()}
        </div>

        {/* Footer */}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, padding: "12px 24px 20px", borderTop: "1px solid #f3f4f6" }}>
          {currentIndex > 0 && (
            <button onClick={() => setTab(TABS[currentIndex - 1])}
              style={{ padding: "9px 20px", background: "#fff", border: "1px solid #d1d5db", borderRadius: 8, cursor: "pointer", fontSize: 13, color: "#374151" }}>
              Back
            </button>
          )}
          {tab === "Documents" && (
            <button onClick={reset}
              style={{ padding: "9px 20px", background: "#fff", border: "1px solid #d1d5db", borderRadius: 8, cursor: "pointer", fontSize: 13, color: "#374151" }}>
              Cancel
            </button>
          )}
          {currentIndex < TABS.length - 1 ? (
            <button onClick={() => setTab(TABS[currentIndex + 1])}
              style={{ padding: "9px 22px", background: "#d97706", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 500 }}>
              Next
            </button>
          ) : (
            <button onClick={() => setSubmitted(true)}
              style={{ padding: "9px 22px", background: "#16a34a", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 500 }}>
              Save Tender
            </button>
          )}
        </div>

      </div>
    </div>
  );
}
